/**
 * The shape one player input's outcome takes (architecture.md §3.3) — split
 * from `process-player-input.ts` at the 500-line cap (D226), and re-exported
 * from it, so the type lives where it is read and the pipeline file keeps
 * the pipeline.
 *
 * @packageDocumentation
 */

import type { RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';
import type { ClarificationRequest, ParseDiagnostics, TokenCorrection } from '@orpheus/parser-engine';

import type { ActionRejection } from '../actions/runtime-action.js';
import type { HostRequest } from './chrome-directives.js';
import type { TurnExecutionResult } from './execute-turn.js';

/**
 * How one player input ended (architecture.md §3.3, amended by D23: every
 * arm carries the committed `nextState` and this input's appended
 * `transcriptEntries`; `executed` carries them inside its aggregate result).
 *
 * `corrections` (R7) is the aggregate of every fuzzy fix applied across the
 * input's parsed clauses — how the parser READ the input, surfaced so UIs
 * can echo "(reading the typo as keycard)" with source spans intact
 * (architecture.md §4.5). Present on every arm; empty when nothing was
 * corrected.
 */
export type PlayerInputOutcome =
	| {
		/**
		 * D27: the whole input defeated the parser AND matched a pack-authored
		 * reaction — the egg line is committed in place of the teach. No turn
		 * consumed, no world change; reactions are voice, not mechanics.
		 */
		readonly corrections: readonly TokenCorrection[];
		/**
		 * D179: a `manual`-mode `save` or `load` — the engine has said its line
		 * and now asks the host to write the one slot, or bring it back. Absent
		 * on every other reaction.
		 */
		readonly hostRequest?: HostRequest;
		readonly nextState: RuntimeState;
		readonly transcriptEntries: readonly RuntimeTranscriptEntry[];
		readonly type: 'reaction';
	}
	| {
		/**
		 * R11: a TERMINAL ending has been selected and the world is closed —
		 * the input is echoed and answered with the ending's own
		 * `refusalNarratives` (or the `storyHasEnded` slot), and nothing else
		 * happens. No turn consumed, no parse attempted: a closed world does
		 * not read commands, it declines them.
		 */
		readonly corrections: readonly TokenCorrection[];
		readonly nextState: RuntimeState;
		readonly transcriptEntries: readonly RuntimeTranscriptEntry[];
		readonly type: 'session_ended';
	}
	| {
		readonly clarification: ClarificationRequest;
		readonly corrections: readonly TokenCorrection[];
		readonly nextState: RuntimeState;
		readonly transcriptEntries: readonly RuntimeTranscriptEntry[];
		readonly type: 'clarification_required';
	}
	| {
		readonly corrections: readonly TokenCorrection[];
		readonly diagnostics: ParseDiagnostics;
		readonly nextState: RuntimeState;
		readonly transcriptEntries: readonly RuntimeTranscriptEntry[];
		readonly type: 'parse_failed';
	}
	| {
		readonly corrections: readonly TokenCorrection[];
		readonly nextState: RuntimeState;
		/**
		 * F5 (2026-08-22): the folded text of the CLAUSE the refusal stopped.
		 * The conversation wrapper parks questions against THIS, never the
		 * whole input — a parked whole input re-ran its prefix when the answer
		 * spliced, and a prefix clause with effects fired twice. REQUIRED
		 * since R21 paired it with the rejection at the walk (the one place a
		 * refusal is constructed always knows its clause).
		 */
		readonly rejectedClauseText: string;
		readonly rejection: ActionRejection;
		readonly transcriptEntries: readonly RuntimeTranscriptEntry[];
		readonly type: 'action_rejected';
	}
	| {
		readonly corrections: readonly TokenCorrection[];
		readonly result: TurnExecutionResult;
		readonly type: 'executed';
	};

/**
 * The entries one input stamped, from whichever arm answered it — the executed arm keeps them on its result.
 *
 * @param outcome - One input's outcome
 * @returns What it said, in order, the player's echo included
 */
export const transcriptEntriesOf = (outcome: PlayerInputOutcome): readonly RuntimeTranscriptEntry[] =>
	outcome.type === 'executed' ? outcome.result.transcriptEntries : outcome.transcriptEntries;
